/**
 * Device Replacement History Component
 * แสดงประวัติการเปลี่ยนอุปกรณ์/SIM ในสัญญาเช่า
 *
 * รองรับการแสดงผล 3 โหมด:
 * - DEVICE_ONLY: เปลี่ยนเฉพาะอุปกรณ์
 * - SIM_ONLY: เปลี่ยนเฉพาะ SIM
 * - BOTH: เปลี่ยนทั้งอุปกรณ์และ SIM
 */
import React, { useState, useEffect } from 'react';
import { usePermission } from '../../hooks/usePermission';
import { ArrowsRightLeftIcon, ArrowRightIcon, ClockIcon, DevicePhoneMobileIcon, SignalIcon } from '@heroicons/react/24/outline';
import { RiExchangeLine } from 'react-icons/ri';
import deviceReplacementService, {
  DeviceReplacementHistoryItem,
  ReplacementType,
  REPLACEMENT_TYPES,
  DEVICE_CONDITIONS,
  SIM_CONDITIONS,
} from '../../services/deviceReplacementService';

interface DeviceReplacementHistoryProps {
  rentalId: number;
}

// Device condition labels (for backward compatibility)
const DEVICE_CONDITION_LABELS: { [key: string]: { label: string; color: string } } = {
  DAMAGED: { label: 'ชำรุด', color: 'bg-danger-100 text-danger-800' },
  LOST: { label: 'สูญหาย', color: 'bg-warning-100 text-warning-800' },
  MAINTENANCE: { label: 'ซ่อมบำรุง', color: 'bg-info-100 text-info-800' },
};

// SIM condition labels
const SIM_CONDITION_LABELS: { [key: string]: { label: string; color: string } } = {
  SIM_EXPIRED: { label: 'SIM หมดอายุ', color: 'bg-warning-100 text-warning-800' },
  SIM_FAULTY: { label: 'SIM เสีย', color: 'bg-danger-100 text-danger-800' },
  SIM_CHANGE_OPERATOR: { label: 'เปลี่ยนค่าย', color: 'bg-accent-100 text-accent-800' },
  SIM_UPGRADE: { label: 'อัพเกรด', color: 'bg-info-100 text-info-800' },
  SIM_DATA_ISSUE: { label: 'ปัญหาสัญญาณ', color: 'bg-warning-100 text-warning-800' },
  SIM_NORMAL_WITH_DEVICE: { label: 'เดิมปกติ (พร้อมเครื่องหลัก)', color: 'bg-success-100 text-success-800' },
};

// Replacement type badge config with React Icons
const REPLACEMENT_TYPE_BADGES: { [key: string]: { label: string; iconType: 'device' | 'sim' | 'both'; color: string } } = {
  DEVICE_ONLY: { label: 'เปลี่ยนอุปกรณ์', iconType: 'device', color: 'bg-info-50 text-info-700 border-info-200' },
  SIM_ONLY: { label: 'เปลี่ยน SIM', iconType: 'sim', color: 'bg-success-50 text-success-700 border-success-200' },
  BOTH: { label: 'เปลี่ยนทั้งคู่', iconType: 'both', color: 'bg-accent-50 text-accent-700 border-accent-200' },
};

// Icon component mapping
const ReplacementTypeIcon: React.FC<{ iconType: 'device' | 'sim' | 'both'; className?: string }> = ({ iconType, className = 'w-4 h-4' }) => {
  switch (iconType) {
    case 'device':
      return <DevicePhoneMobileIcon className={className} />;
    case 'sim':
      return <SignalIcon className={className} />;
    case 'both':
      return <RiExchangeLine className={className} />;
  }
};

const DeviceReplacementHistory: React.FC<DeviceReplacementHistoryProps> = ({
  rentalId,
}) => {
  /**
   * `GET /device-replacements/rentals/{id}/replacement-history` gate ด้วย
   * `device_replacements.view` — **ต้องเช็คก่อนยิง** (บั๊กเดียวกับ BUG-UI-09)
   * บทบาทบัญชีมี `rentals.view` แต่ไม่มีสิทธิ์นี้ → เปิดหน้ารายละเอียดการเช่าแล้วได้ 403
   */
  const { hasPermission } = usePermission();
  const canView = hasPermission('device_replacements.view');
  const [history, setHistory] = useState<DeviceReplacementHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadHistory = async () => {
      if (!rentalId) return;
      if (!canView) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await deviceReplacementService.getRentalReplacementHistory(rentalId);
        setHistory(data);
      } catch (err) {
        console.error('Error loading replacement history:', err);
        setError('ไม่สามารถโหลดประวัติการเปลี่ยนอุปกรณ์');
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [rentalId, canView]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ไม่มีสิทธิ์ → ซ่อนการ์ดทั้งใบ (อย่าโชว์การ์ด error ที่อ่านเหมือนระบบพัง)
  if (!canView) return null;

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        <div className="px-6 py-4 border-b border-secondary-200">
          <h2 className="text-lg font-medium text-secondary-900 flex items-center">
            <ArrowsRightLeftIcon className="w-5 h-5 mr-2 text-secondary-500" />
            ประวัติการเปลี่ยนอุปกรณ์
          </h2>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="ml-2 text-secondary-500">กำลังโหลด...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        <div className="px-6 py-4 border-b border-secondary-200">
          <h2 className="text-lg font-medium text-secondary-900 flex items-center">
            <ArrowsRightLeftIcon className="w-5 h-5 mr-2 text-secondary-500" />
            ประวัติการเปลี่ยนอุปกรณ์
          </h2>
        </div>
        <div className="p-6">
          <p className="text-danger-500 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
      <div className="px-6 py-4 border-b border-secondary-200">
        <h2 className="text-lg font-medium text-secondary-900 flex items-center">
          <ArrowsRightLeftIcon className="w-5 h-5 mr-2 text-secondary-500" />
          ประวัติการเปลี่ยนอุปกรณ์
          {history.length > 0 && (
            <span className="ml-2 bg-secondary-100 text-secondary-600 text-sm px-2 py-0.5 rounded-full">
              {history.length}
            </span>
          )}
        </h2>
      </div>
      <div className="p-6">
        {history.length === 0 ? (
          <div className="text-center py-8 text-secondary-500">
            <ArrowsRightLeftIcon className="w-12 h-12 mx-auto text-secondary-300 mb-3" />
            <p>ยังไม่มีประวัติการเปลี่ยนอุปกรณ์</p>
          </div>
        ) : (
          <div className="space-y-4">
            {history.map((item) => {
              // Determine replacement type (default to DEVICE_ONLY for backward compatibility)
              const replacementType = item.replacement_type || 'DEVICE_ONLY';
              const typeBadge = REPLACEMENT_TYPE_BADGES[replacementType] || REPLACEMENT_TYPE_BADGES.DEVICE_ONLY;

              // Check which sections to show
              const showDeviceSection = replacementType === 'DEVICE_ONLY' || replacementType === 'BOTH';
              const showSimSection = replacementType === 'SIM_ONLY' || replacementType === 'BOTH';

              return (
                <div
                  key={item.id}
                  className="border border-secondary-200 rounded-lg p-4 hover:bg-secondary-50 transition-colors"
                >
                  {/* Header: Date, Replacement Type Badge & Condition */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center text-sm text-secondary-500">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        {formatDate(item.replacement_date)}
                      </div>
                      {/* Replacement Type Badge */}
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border ${typeBadge.color}`}
                      >
                        <ReplacementTypeIcon iconType={typeBadge.iconType} className="w-3.5 h-3.5 mr-1" />
                        {typeBadge.label}
                      </span>
                    </div>

                    {/* Condition Badge(s) */}
                    <div className="flex items-center gap-2">
                      {/* Device Condition (for DEVICE_ONLY or BOTH) */}
                      {showDeviceSection && item.old_device_condition && (
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            DEVICE_CONDITION_LABELS[item.old_device_condition]?.color || 'bg-secondary-100 text-secondary-800'
                          }`}
                        >
                          {DEVICE_CONDITION_LABELS[item.old_device_condition]?.label || item.old_device_condition}
                        </span>
                      )}
                      {/* SIM Condition (for SIM_ONLY or BOTH) */}
                      {showSimSection && item.old_sim_condition && (
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            SIM_CONDITION_LABELS[item.old_sim_condition]?.color || 'bg-secondary-100 text-secondary-800'
                          }`}
                        >
                          {SIM_CONDITION_LABELS[item.old_sim_condition]?.label || item.old_sim_condition}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Device Change Section (for DEVICE_ONLY or BOTH) */}
                  {showDeviceSection && (
                    <div className="flex items-center mb-3">
                      <div className="flex-1">
                        <p className="text-sm text-secondary-500">อุปกรณ์เดิม</p>
                        <p className="font-medium text-secondary-900">
                          {item.old_device_code || 'ไม่ระบุ'}
                        </p>
                        {item.old_device_serial && (
                          <p className="text-xs text-secondary-500">S/N: {item.old_device_serial}</p>
                        )}
                      </div>

                      <ArrowRightIcon className="w-5 h-5 text-secondary-400 mx-4" />

                      <div className="flex-1">
                        <p className="text-sm text-secondary-500">อุปกรณ์ใหม่</p>
                        <p className="font-medium text-success-600">
                          {item.new_device_code || 'ไม่ระบุ'}
                        </p>
                        {item.new_device_serial && (
                          <p className="text-xs text-secondary-500">S/N: {item.new_device_serial}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* SIM Change Section (for SIM_ONLY or BOTH) */}
                  {showSimSection && (
                    <div className={`flex items-center mb-3 ${replacementType === 'SIM_ONLY' ? '' : 'bg-secondary-50 rounded-md p-2'}`}>
                      <div className="flex-1">
                        <p className="text-sm text-secondary-500">SIM เดิม</p>
                        <p className={`${replacementType === 'SIM_ONLY' ? 'font-medium text-secondary-900' : 'text-sm text-secondary-700'}`}>
                          {item.old_sim_number || '-'}
                        </p>
                      </div>

                      <ArrowRightIcon className={`${replacementType === 'SIM_ONLY' ? 'w-5 h-5 mx-4' : 'w-4 h-4 mx-2'} text-secondary-400`} />

                      <div className="flex-1">
                        <p className="text-sm text-secondary-500">SIM ใหม่</p>
                        <p className={`${replacementType === 'SIM_ONLY' ? 'font-medium' : 'text-sm'} text-success-600`}>
                          {item.new_sim_number || '-'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* AnyDesk Change (DEVICE_ONLY / BOTH) — แสดงเมื่อมีค่าฝั่งใดฝั่งหนึ่ง */}
                  {showDeviceSection && (item.old_anydesk_id || item.new_anydesk_id) && (
                    <div className="flex items-center mb-3 bg-indigo-50 rounded-md p-2">
                      <div className="flex-1">
                        <p className="text-sm text-secondary-500">AnyDesk เดิม</p>
                        <p className="text-sm font-mono text-secondary-700">{item.old_anydesk_id || '-'}</p>
                      </div>

                      <ArrowRightIcon className="w-4 h-4 mx-2 text-secondary-400" />

                      <div className="flex-1">
                        <p className="text-sm text-secondary-500">AnyDesk ใหม่</p>
                        {item.new_anydesk_id ? (
                          <p className="text-sm font-mono text-success-600">{item.new_anydesk_id}</p>
                        ) : (
                          <p className="text-sm text-secondary-400 italic">ล้างค่า</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Reason */}
                  <div className="mb-2">
                    <p className="text-sm text-secondary-500">เหตุผล:</p>
                    <p className="text-sm text-secondary-700 break-words">{item.reason}</p>
                  </div>

                  {/* Notes */}
                  {item.notes && (
                    <div className="mb-2">
                      <p className="text-sm text-secondary-500">หมายเหตุ:</p>
                      <p className="text-sm text-secondary-600 italic break-words">{item.notes}</p>
                    </div>
                  )}

                  {/* Replaced By */}
                  <div className="text-xs text-secondary-400 mt-2 pt-2 border-t border-secondary-100">
                    โดย: {item.replaced_by_name || 'ไม่ระบุ'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceReplacementHistory;
